import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { environment } from '@env/fe';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule } from '@ngrx/store';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

import { TranslationService, TxNativeModule } from '@transifex/angular';
import {
  AuthService,
  StorageLevelToCustomerLevelNamePipe,
  SystemsService,
  UserTrackingService,
} from '@twaice-fe/frontend/shared/services';
import {
  DemoLinkModule,
  FooterModule,
  NavigationMenuModule,
  PackageFeatureDirective,
  SideNavLayoutModule,
  SvgIconModule,
  TwRouterLinkDirective,
} from '@twaice-fe/frontend/shared/ui-components';
import { FeatureToggleModule } from 'ngx-feature-toggle';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { DashboardLayoutComponent } from './containers/dashboard-layout/dashboard-layout.component';
import { ToolbarComponent } from './containers/dashboard-layout/toolbar/toolbar.component';
import { AuthorizationTokenInterceptor } from './utilities/authorisation.interceptor';
import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { MissingConfigurationComponent } from './views/error/missing-configuration.component';
import { ProfessionalServicesModule } from './views/professional-services/professional-services.module';
import { SolutionModule } from './views/solution/solution.module';
import { SubPageModule } from './views/sub-page/sub-page.module';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

import { clearStateMetaReducer, effects, reducers } from '@twaice-fe/frontend/shared/store';

const { systemReducer, configsReducer, authReducer, chatReducer } = reducers;

const { SystemsEffects, AuthEffects, ConfigEffects, ChatEffects } = effects;

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { GlobalErrorHandler } from '@twaice-fe/frontend/shared/utilities';
import { CustomerConfigurationService } from '@twaice-fe/frontend/shared/services';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EnergyAnalyticsEffects, UserTrackingEffects } from 'libs/frontend/shared/store/src/effects';
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { energyAnalyticsReducer } from 'libs/frontend/shared/store/src/reducers';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { en_GB, NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import enGB from 'date-fns/locale/en-GB';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DashboardSideNavComponent } from './containers/dashboard-layout/sidenav/dashboard-sidenav.component';
import { RequestedSystemsInterceptor } from './utilities/requested-systems.interceptor';
import { AnalyticsUpsellComponent } from './views/analytics-upsell/analytics-upsell.component';

export function initApp(auth: AuthService, systemService: SystemsService) {
  return async () => {
    try {
      await auth.waitForAuthComplete();

      if (auth.isLoggedIn()) {
        try {
          await systemService.getCurrentSystemUiConfiguration();
        } catch (error) {
          console.error('[AppModule] Error while preparing router configuration: ', error);
        }
      }
    } catch (error) {
      console.error('[AppModule] Error waiting for auth completion:', error);
    }

    return true;
  };
}

registerLocaleData(localeDe, 'de-DE', localeDeExtra);
registerLocaleData(localeDe, 'de', localeDeExtra);

export const metaReducers: MetaReducer[] = [clearStateMetaReducer];

const COMMON = [BrowserModule, BrowserAnimationsModule, RouterModule, AppRoutingModule, HttpClientModule, CommonModule];

const MATERIAL_MODULES = [
  MatTabsModule,
  MatToolbarModule,
  MatIconModule,
  MatButtonModule,
  MatMenuModule,
  MatSnackBarModule,
  MatAutocompleteModule,
  MatFormFieldModule,
  MatInputModule,
];

const INTERNAL_MODULES = [
  FeatureToggleModule,
  FooterModule,
  SubPageModule,
  SolutionModule,
  MatDividerModule,
  SvgIconModule,
  NavigationMenuModule,
  SideNavLayoutModule,
  DemoLinkModule,
  TwRouterLinkDirective,
  PackageFeatureDirective,
  ProfessionalServicesModule,
];

const ANT_MODULES = [
  NzLayoutModule,
  NzMessageModule,
  NzMenuModule,
  NzToolTipModule,
  NzLayoutModule,
  NzDropDownModule,
  NzButtonModule,
  NzTagModule,
  NzCardModule,
  NzTabsModule,
  NzBadgeModule,
  NzCheckboxModule,
  FormsModule,
  NzDrawerModule,
];

@NgModule({
  imports: [
    TxNativeModule.forRoot(),
    ...COMMON,
    ...MATERIAL_MODULES,
    ...INTERNAL_MODULES,
    ...ANT_MODULES,
    StoreModule.forRoot(
      {
        router: routerReducer,
        [systemReducer.SYSTEMS_FEATURE_KEY]: systemReducer.reducer,
        [chatReducer.CHAT_FEATURE_KEY]: chatReducer.reducer,
        [configsReducer.CONFIGS_FEATURE_KEY]: configsReducer.reducer,
        [authReducer.AUTH_FEATURE_KEY]: authReducer.reducer,
        [energyAnalyticsReducer.ENERGY_ANALYTICS_FEATURE_KEY]: energyAnalyticsReducer.energyAnalyticsReducer,
      },
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([SystemsEffects, AuthEffects, UserTrackingEffects, EnergyAnalyticsEffects, ConfigEffects, ChatEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      connectInZone: true,
    }),
    StoreRouterConnectingModule.forRoot(),
    NgOptimizedImage,
  ],
  declarations: [
    AppComponent,
    P404Component,
    P500Component,
    MissingConfigurationComponent,
    DashboardLayoutComponent,
    DashboardSideNavComponent,
    ToolbarComponent,
    AnalyticsUpsellComponent,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initApp(inject(AuthService), inject(SystemsService));
      return initializerFn();
    }),
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useFactory: (router: Router) => new GlobalErrorHandler(router, { showDialog: false }),
      deps: [Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestedSystemsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationTokenInterceptor,
      multi: true,
    },
    { provide: NZ_I18N, useValue: en_GB },
    { provide: NZ_DATE_LOCALE, useValue: enGB },
    {
      provide: NZ_DATE_CONFIG,
      useValue: {
        firstDayOfWeek: 1, // week starts on Monday (Sunday is 0)
      },
    },
    StorageLevelToCustomerLevelNamePipe,
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    CustomerConfigurationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private translationService: TranslationService,
    private userTrackingService: UserTrackingService
  ) {
    // init Transifex config
    this.translationService.init({
      token: environment.transifexToken,
    });

    this.userTrackingService.setupMixpanelTracking(environment.mixpanelId);
    this.userTrackingService.setupHubspotIntegration(environment.hubspotPortalId);
  }
}
